import React, { useState, useRef } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { StyledInputField } from "@root/commonStyles/commonStyle";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { contactUs } from "@root/store/thunks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustonButtom } from "../common/button/customButton";
import { useNotification } from "@root/hooks/useNotification";
import { ALLOWED_FORMATS_FOR_CONTACT_US } from "@root/webConstants";

interface ContactUsFormValues {
  full_name: string;
  email: string;
  phone: string;
  message: string;
  document: File | null;
}

interface ContactUsFormProps {
  onClose?: () => void;
}

export const ContactUsForm: React.FC<ContactUsFormProps> = ({ onClose }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { showNotification } = useNotification()

  const initialValues: ContactUsFormValues = {
    full_name: "",
    email: "",
    phone: "",
    message: "",
    document: null,
  };

  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required(t("validation.fullNameRequired")),
    email: Yup.string()
      .email(t("validation.emailInvalid"))
      .required(t("validation.emailRequired")),
    phone: Yup.string()
      .matches(/^[0-9]+$/, t("validation.phoneNumeric"))
      .required(t("validation.phoneRequired")),
    message: Yup.string().required(t("validation.messageRequired")),
  });

  const handleSubmit = (
    values: ContactUsFormValues,
    actions: FormikHelpers<ContactUsFormValues>
  ) => {
    dispatch(
      contactUs({
        payload: values,
        selectedFile: selectedFile,
        sucessCallback: () => {
          successCallback(actions);
          if (onClose) {
            onClose()
          }
        },
        errorCallback: (error: any) => {
          if (onClose) {
            onClose()
          }
          console.error("Error submitting form:", error);
          actions.setSubmitting(false);
          console.log("Error callback called, setting isSubmitting to false");
        },
      })
    );
  };

  const successCallback = (actions: FormikHelpers<ContactUsFormValues>) => {
    actions.setSubmitting(false);
    navigate(-1);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileLoad = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit,
        errors,
        touched,
        values,
        setFieldValue,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <VStack
            spacing={"16px"}
            align="flex-start"
            borderColor={"gray.200"}
            padding={"10px"}
          >
            <FormLabel htmlFor="full_name">{t("signup.fullName")}</FormLabel>
            <FormControl isInvalid={!!errors.full_name && touched.full_name}>
              <InputGroup>
                <StyledInputField
                  onChange={(e: any) =>
                    setFieldValue("full_name", e.target.value)
                  }
                  borderColor={values.full_name ? "primary.200" : "gray.200"}
                  as={Input}
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  variant="filled"
                  placeholder={t("contact.fullName")}
                  icon
                />
              </InputGroup>
              <FormErrorMessage>{errors.full_name}</FormErrorMessage>
            </FormControl>

            <FormLabel htmlFor="emailAddress">{t("login.email")}</FormLabel>
            <FormControl isInvalid={!!errors.email && touched.email}>
              <StyledInputField
                onChange={(e: any) => setFieldValue("email", e.target.value)}
                borderColor={"gray.200"}
                as={Input}
                id="email"
                name="email"
                type="text"
                required
                variant="filled"
                placeholder={t("contact.email")}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormLabel htmlFor="phone" margin={"0px"}>
              {t("contact.phoneNumber")}
            </FormLabel>
            <FormControl isInvalid={!!errors.phone && touched.phone}>
              <InputGroup>
                <StyledInputField
                  as={Input}
                  onChange={(e: any) => setFieldValue("phone", e.target.value)}
                  id="phone"
                  name="phone"
                  type="text"
                  borderColor={"gray.200"}
                  required
                  variant="filled"
                  maxW={"900px"}
                  placeholder={t("contact.phone")}
                  onKeyPress={handleKeyPress}
                  pattern="[0-9]*"
                />
              </InputGroup>
              <FormErrorMessage>{errors.phone}</FormErrorMessage>
            </FormControl>

            <FormLabel htmlFor="message" margin={"0px"}>
              {t("contact.message")}
            </FormLabel>
            <FormControl isInvalid={!!errors.message && touched.message}>
              <InputGroup>
                <StyledInputField
                  resize={"none"}
                  as={Textarea}
                  onChange={(e: any) =>
                    setFieldValue("message", e.target.value)
                  }
                  id="message"
                  name="message"
                  type="text"
                  required
                  variant="filled"
                  placeholder={t("contact.writeMessage")}
                />
              </InputGroup>
              <FormErrorMessage>{errors.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.document && touched.document}>
              <InputGroup position="relative">
                <StyledInputField
                  as={Input}
                  readOnly
                  value={values.document ? values.document.name : ""}
                  placeholder={t("contact.uploadDocument")}
                  onClick={handleFileUploadClick}
                  cursor="pointer"
                  borderColor={"gray.200"}
                />
                <Text
                  position="absolute"
                  top="50%"
                  right="16px"
                  transform="translateY(-50%)"
                  color="blue.500"
                  cursor="pointer"
                  size="12px"
                  fontWeight="700"
                  onClick={handleFileUploadClick}
                >
                  {t("contact.upload")}
                </Text>
                <Input
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files ? e.target.files[0] : null;

                    if (file) {
                      const allowedFormats = ["image/png", "image/jpeg", 'image/jpg'];
                      if (!allowedFormats.includes(file.type)) {
                        showNotification({
                          type: 'error',
                          message: t('translate.allowedFormat', { formats: ALLOWED_FORMATS_FOR_CONTACT_US.join(', ') })
                        })
                        return; // Exit the function if the format is not allowed
                      }

                      handleFileLoad(e);
                      setFieldValue("document", file);
                    }

                    handleFileLoad(e);
                    setFieldValue(
                      "document",
                      e.target.files ? e.target.files[0] : null
                    );
                  }}
                  id="document"
                  name="document"
                />
              </InputGroup>
              <FormErrorMessage>{errors.document}</FormErrorMessage>
            </FormControl>

            <Flex justifyContent="center" width="full">
              <CustonButtom
                type="submit"
                isLoading={isSubmitting}
                text={t("contact.submit")}
              />
            </Flex>
          </VStack>
        </form>
      )}
    </Formik>
  );
};
